import { spineStaticOriginalUrl } from "commons/cdn";
import { getTemplateOrder } from "../types/spine-template";

function isHttpUrl(s?: string): s is string {
  if (!s || typeof s !== "string") return false;
  return (
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    s.startsWith("//")
  );
}

export type StaticStackItem =
  | {
      kind: "img";
      key: string;
      url: string;
      zIndex: number;
    }
  | { kind: "canvas"; key: string; zIndex: number };

/** 与 TEMPLATE_ORDER 同量级：保证 bg < character(canvas) < fg */
const ORDER_SHIFT = 10_000;

/**
 * 单层静态图 z-index：模版类型顺序 + 关联模版在 relatedTemplateIds 中的顺序 + playIndex
 */
export function zIndexForStaticLayer(
  templateType: string | undefined,
  playIndex: number,
  /** 主模版固定为 0；关联模版为在 relatedTemplateIds 中的下标（越大越靠上） */
  relatedStackIndex: number,
): number {
  const o = getTemplateOrder(templateType);
  return (
    (o + ORDER_SHIFT) * 1_000 +
    relatedStackIndex * 10_000 +
    playIndex * 100
  );
}

/** 主 Spine 画布（character 档） */
export function zIndexForSpineCanvas(): number {
  const o = getTemplateOrder("character");
  return (o + ORDER_SHIFT) * 1_000 + 500;
}

/** 头像模版 background 层：低于 Spine bg、bgEffect */
export function zIndexForAvatarTemplateBg(layerZ = 0): number {
  const AVATAR_BG_ORDER = -250;
  return (AVATAR_BG_ORDER + ORDER_SHIFT) * 1_000 + layerZ;
}

/** 头像模版非 background 层 Konva：与 character 档 Spine 同栈，略靠上 */
export function zIndexForAvatarTemplateCharacterKonva(): number {
  return zIndexForSpineCanvas() + 50;
}

/** 头像编辑器：人物 Konva 之上的关联 Spine（bgEffect / fg 等） */
export function zIndexForSpineCanvasAboveAvatar(): number {
  return zIndexForAvatarTemplateCharacterKonva() + 50;
}

/**
 * 从 templateConfig 解析每层展示用 URL（预览列表点选与叠放一致）
 */
export function buildStaticLayerRows(
  templateConfig: { selection?: any[]; skus?: Record<string, any> } | null | undefined,
  /** 传入时：仅当该 selection 下有已选中的 SKU（skinName ∈ selectedSkins）时才渲染该层，与列表里点选一致 */
  selectedSkins?: string[] | null,
): { key: string; url: string; playIndex: number }[] {
  if (!templateConfig?.selection?.length) return [];
  const skus = templateConfig.skus || {};
  const sorted = [...templateConfig.selection].sort(
    (a, b) => (a.playIndex ?? 0) - (b.playIndex ?? 0),
  );
  const out: { key: string; url: string; playIndex: number }[] = [];
  for (const sel of sorted) {
    const skuIdsList = sel.skuIds || [];
    const skuIdsOrdered =
      selectedSkins != null
        ? skuIdsList.filter((id) => {
            const sku = skus[id];
            return sku && selectedSkins.includes(sku.skinName);
          })
        : skuIdsList;

    if (selectedSkins != null && skuIdsOrdered.length === 0) {
      continue;
    }

    let url: string | undefined;
    for (const skuId of skuIdsOrdered) {
      const sku = skus[skuId];
      if (sku?.originalAssetId) {
        url = spineStaticOriginalUrl(sku.originalAssetId);
        break;
      }
    }
    if (!url && isHttpUrl(sel.layerIconId)) {
      url = sel.layerIconId;
    }
    if (url) {
      out.push({
        key: `${sel.id}-${url}`,
        url,
        playIndex: sel.playIndex ?? 0,
      });
    }
  }
  return out;
}

/**
 * 合并主模版静态层 + 关联静态模版层，带全局 z-index，用于与 Spine 画布同栈叠放
 */
export function buildMergedStaticStackItems(args: {
  /** 当前主模版为静态时 */
  mainTemplateType: string | undefined;
  mainTemplateConfig: { selection?: any[]; skus?: Record<string, any> } | null | undefined;
  /** spineData 为空的关联静态模版 */
  relatedStatic: {
    id: string;
    type?: string;
    config: { selection?: any[]; skus?: Record<string, any> };
  }[];
  /** 与 buildStaticLayerRows：仅展示已选中 SKU 对应的静态层 */
  selectedSkins: string[];
  /** 关联模版 id -> 在 relatedTemplateIds 中的下标 */
  relatedTemplateOrderIndexById?: Map<string, number>;
}): StaticStackItem[] {
  const items: StaticStackItem[] = [];
  const {
    mainTemplateType,
    mainTemplateConfig,
    relatedStatic,
    selectedSkins,
    relatedTemplateOrderIndexById,
  } = args;

  if (mainTemplateConfig) {
    for (const row of buildStaticLayerRows(mainTemplateConfig, selectedSkins)) {
      items.push({
        kind: "img",
        key: `main-${row.key}`,
        url: row.url,
        zIndex: zIndexForStaticLayer(mainTemplateType, row.playIndex, 0),
      });
    }
  }

  for (const rel of relatedStatic) {
    const stackIdx = relatedTemplateOrderIndexById?.get(rel.id) ?? 0;
    for (const row of buildStaticLayerRows(rel.config, selectedSkins)) {
      items.push({
        kind: "img",
        key: `${rel.id}-${row.key}`,
        url: row.url,
        zIndex: zIndexForStaticLayer(rel.type, row.playIndex, stackIdx),
      });
    }
  }

  return items;
}
